/**
 * Firebase auth helpers and backend session bootstrap.
 */

import {
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  UserCredential,
} from 'firebase/auth';
import { auth } from '@/config/firebase';
import apiClient from './api-client';
import type { User } from '@/types/user.types';

/** Firebase email/password sign-in. */
export const loginWithEmail = async (email: string, password: string): Promise<UserCredential> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error) {
    console.error('Error logging in with email:', error);
    throw error;
  }
};

/** Firebase phone OTP send (`signInWithPhoneNumber`). */
export const sendPhoneOtp = async (
  phoneNumber: string,
  recaptchaVerifier: RecaptchaVerifier,
): Promise<ConfirmationResult> => {
  try {
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    return confirmationResult;
  } catch (error) {
    console.error('Error sending phone OTP:', error);
    throw error;
  }
};

/** Confirm a Firebase phone OTP. */
export const verifyPhoneOtp = async (
  confirmationResult: ConfirmationResult,
  code: string,
): Promise<UserCredential> => {
  try {
    const userCredential = await confirmationResult.confirm(code);
    return userCredential;
  } catch (error) {
    console.error('Error verifying phone OTP:', error);
    throw error;
  }
};

/**
 * `POST /users/activate` — set password from an invitation token.
 * Public endpoint — no Firebase auth required.
 */
export const activateAccount = async (
  token: string,
  password: string,
): Promise<{ activated: boolean; email: string; roleGroup: string | null }> => {
  const { data } = await apiClient.post('/users/activate', { token, password });
  return data;
};

/**
 * `POST /users/forgot-password` — resend invite if pending, else Firebase reset.
 * Always succeeds for a valid email so the UI cannot leak whether the account exists.
 */
export const sendPasswordResetEmail = async (email: string): Promise<void> => {
  await apiClient.post('/users/forgot-password', { email: email.trim().toLowerCase() });
};

/** `GET /users/login` — staff profile after Firebase authentication. */
export const fetchUserData = async (): Promise<User> => {
  try {
    const { data } = await apiClient.get('/users/login');
    return data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

/** `POST /auth/logout` — revoke the Firebase refresh token server-side. */
export const logoutFromBackend = async (): Promise<void> => {
  await apiClient.post('/auth/logout');
};
